import { AlertCircle, Calendar, CheckCircle, ChevronDown, ChevronUp, CreditCard, HelpCircle, Mail, MessageSquare, Phone, Search, Settings } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';

const SupportFAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openCategories, setOpenCategories] = useState(['getting-started']);
  const [openQuestions, setOpenQuestions] = useState([]);
  const { site } = useConfig();

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: HelpCircle,
      questions: [
        {
          id: 'setup-time',
          question: 'How long does it take to set up Booker.Chat?',
          answer: 'Most businesses are up and running within 5-10 minutes. Our setup wizard guides you through connecting your phone number, WhatsApp Business account, and Google Calendar. We also provide live chat support during setup if you need help.'
        },
        {
          id: 'trial-process',
          question: 'How does the 14-day free trial work?',
          answer: 'You get full access to your selected plan for 14 days with no credit card required. During the trial, you can handle unlimited bookings, test all features, and get full support. After 14 days, you can choose to continue with a paid subscription or cancel with no charges.'
        },
        {
          id: 'requirements',
          question: 'What do I need to get started?',
          answer: 'You need: (1) A business phone number, (2) WhatsApp Business account (we can help set this up), (3) Google Calendar or similar calendar system, and (4) Basic information about your services and operating hours. That\'s it!'
        },
        {
          id: 'business-types',
          question: 'Does Booker.Chat work for my type of business?',
          answer: 'Booker.Chat is perfect for appointment-based businesses including hair salons, barbershops, nail salons, spas, beauty studios, wellness centers, dental offices, and similar service businesses. If customers book appointments with you, we can help automate the process.'
        }
      ]
    },
    {
      id: 'technical-setup',
      title: 'Technical Setup',
      icon: Settings,
      questions: [
        {
          id: 'whatsapp-setup',
          question: 'How do I connect my WhatsApp Business account?',
          answer: 'During setup, we\'ll guide you through the WhatsApp Business API connection. If you don\'t have WhatsApp Business yet, we\'ll help you set it up. The process involves verifying your business phone number and confirming your business information with WhatsApp.'
        },
        {
          id: 'calendar-integration',
          question: 'Which calendar systems do you integrate with?',
          answer: 'We primarily integrate with Google Calendar, which syncs with most other systems. If you use Outlook, Apple Calendar, or other systems, they can usually sync with Google Calendar. We also offer direct integrations for popular booking systems upon request.'
        },
        {
          id: 'phone-forwarding',
          question: 'Do I need to change my phone number?',
          answer: 'No! You keep your existing business phone number. We set up smart call routing so booking-related calls go to our AI, while other calls come directly to you. You can adjust these settings anytime.'
        },
        {
          id: 'multiple-locations',
          question: 'Can I use Booker.Chat for multiple business locations?',
          answer: 'Yes! Each location needs its own subscription, but we offer volume discounts for multiple locations. Each location can have its own settings, staff, services, and operating hours.'
        }
      ]
    },
    {
      id: 'billing-pricing',
      title: 'Billing & Pricing',
      icon: CreditCard,
      questions: [
        {
          id: 'pricing-plans',
          question: 'What\'s included in each pricing plan?',
          answer: 'WhatsApp Only ($79): WhatsApp booking, basic analytics. WhatsApp + Voice ($159): Adds phone booking, advanced analytics. + Marketing ($259): Adds promotional campaigns, customer insights. + Payments ($300): Adds payment processing, unlimited bookings. All plans include calendar integration and support.'
        },
        {
          id: 'plan-changes',
          question: 'Can I change my plan later?',
          answer: 'Absolutely! You can upgrade or downgrade anytime. Upgrades take effect immediately, downgrades take effect at your next billing cycle. We\'ll help migrate your settings and ensure no disruption to your service.'
        },
        {
          id: 'cancellation',
          question: 'How do I cancel my subscription?',
          answer: 'You can cancel anytime with 30 days\' notice through your account dashboard or by contacting support. We don\'t believe in long-term contracts - you should stay because you love the service, not because you\'re locked in.'
        },
        {
          id: 'refunds',
          question: 'Do you offer refunds?',
          answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied within the first 30 days, we\'ll refund your payment in full, no questions asked.'
        }
      ]
    },
    {
      id: 'features-functionality',
      title: 'Features & Functionality',
      icon: MessageSquare,
      questions: [
        {
          id: 'ai-accuracy',
          question: 'How accurate is the AI booking assistant?',
          answer: 'Our AI achieves 95%+ accuracy in understanding booking requests. It gets smarter over time by learning your business\'s specific terminology and customer patterns. For complex requests it can\'t handle, it gracefully escalates to your staff while preserving all conversation context.'
        },
        {
          id: 'languages',
          question: 'What languages does the AI support?',
          answer: 'The AI communicates in English, Spanish, French, and Portuguese. It automatically detects the customer\'s preferred language and responds accordingly. We\'re adding more languages based on customer demand.'
        },
        {
          id: 'business-hours',
          question: 'What happens when customers contact me outside business hours?',
          answer: 'The AI works 24/7! It can take bookings for future dates even when you\'re closed. Customers get immediate responses, and you wake up to confirmed appointments in your calendar. The AI respects your operating hours when scheduling appointments.'
        },
        {
          id: 'complex-requests',
          question: 'Can the AI handle complex booking requests?',
          answer: 'Yes! The AI handles multiple services, group bookings, rescheduling, cancellations, and special requests. For anything it can\'t handle, it politely asks customers to call during business hours or escalates to your staff while keeping all context.'
        }
      ]
    },
    {
      id: 'integration-compatibility',
      title: 'Integration & Compatibility',
      icon: Calendar,
      questions: [
        {
          id: 'existing-systems',
          question: 'Will Booker.Chat work with my existing booking system?',
          answer: 'Booker.Chat works alongside most existing systems through calendar integration. If you use specialized salon software, we can often integrate directly. Contact us with your specific system details for a compatibility check.'
        },
        {
          id: 'staff-schedules',
          question: 'How does it handle multiple staff members?',
          answer: 'The AI can assign appointments to specific staff members based on availability, service specialties, and customer preferences. Each staff member can have their own calendar, services, and schedule within the system.'
        },
        {
          id: 'payment-processing',
          question: 'How does payment processing work?',
          answer: 'With our Payments plan, customers can pay deposits or full amounts during booking. We integrate with Stripe for secure payment processing. You can require deposits for certain services or make payments optional.'
        },
        {
          id: 'data-security',
          question: 'How secure is my customer data?',
          answer: 'We take security seriously. All data is encrypted in transit and at rest. We\'re SOC 2 compliant and follow GDPR guidelines. Customer payment information is handled by our PCI-compliant payment processor, not stored on our servers.'
        }
      ]
    },
    {
      id: 'support-troubleshooting',
      title: 'Support & Troubleshooting',
      icon: Phone,
      questions: [
        {
          id: 'support-hours',
          question: 'When is customer support available?',
          answer: 'Email support is available 24/7 with responses within 4 hours. Chat support is available Monday-Friday 8AM-8PM EST. Phone support is available for Business and Enterprise plans during business hours. We also have extensive help documentation and video tutorials.'
        },
        {
          id: 'setup-help',
          question: 'Do you help with setup and training?',
          answer: 'Yes! Every new customer gets personalized onboarding including setup assistance, staff training, and best practices guidance. We also provide ongoing support to optimize your AI assistant\'s performance.'
        },
        {
          id: 'ai-not-working',
          question: 'What if the AI stops responding or makes mistakes?',
          answer: 'Our AI has 99.9% uptime, but if issues occur, we have automatic failsafes that alert our team immediately. For mistakes, the AI learns from corrections, and our team reviews all escalated conversations to improve accuracy.'
        },
        {
          id: 'customization',
          question: 'Can I customize how the AI speaks to customers?',
          answer: 'Yes! You can customize the AI\'s tone, greeting messages, and responses to match your brand voice. Business and Enterprise plans include advanced customization options and branded messaging.'
        }
      ]
    }
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      question =>
        question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">Booker.Chat</span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <a href={`mailto:${site.contact.supportEmail}`}className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Contact Support</a>
                <Link to="/" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Help Center</Link>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                  Start Free Trial
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900">Support & FAQ</h1>
          <p className="mt-4 text-xl text-gray-600">
            Find answers to common questions or get in touch with our support team
          </p>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="bg-blue-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <MessageSquare className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900">Live Chat</h3>
              <p className="text-sm text-gray-600 mb-3">Get instant help with setup and questions</p>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Start Chat
              </button>
            </div>
            <div className="text-center">
              <Mail className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900">Email Support</h3>
              <p className="text-sm text-gray-600 mb-3">Response within 4 hours, 24/7</p>
              <Link to="mailto:support@mockwit.ai" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                support@mockwit.ai
              </Link>
            </div>
            <div className="text-center">
              <Phone className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900">Phone Support</h3>
              <p className="text-sm text-gray-600 mb-3">Business & Enterprise plans</p>
              <Link to="tel:+1-555-123-4567" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                +1 (555) 123-4567
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {filteredCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg shadow">
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <category.icon className="h-6 w-6 text-blue-600 mr-3" />
                    <h2 className="text-xl font-medium text-gray-900">{category.title}</h2>
                    <span className="ml-3 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                      {category.questions.length}
                    </span>
                  </div>
                  {openCategories.includes(category.id) ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {openCategories.includes(category.id) && (
                  <div className="border-t border-gray-200">
                    {category.questions.map((question) => (
                      <div key={question.id} className="border-b border-gray-200 last:border-b-0">
                        <button
                          onClick={() => toggleQuestion(question.id)}
                          className="w-full px-6 py-4 text-left hover:bg-gray-50 flex items-center justify-between"
                        >
                          <h3 className="font-medium text-gray-900 pr-4">{question.question}</h3>
                          {openQuestions.includes(question.id) ? (
                            <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        
                        {openQuestions.includes(question.id) && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700 leading-relaxed">{question.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredCategories.length === 0 && searchTerm && (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600 mb-4">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                Contact Support
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Status & Updates */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">System Status</h2>
            <p className="mt-2 text-gray-600">Current status of Booker.Chat services</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900">WhatsApp Service</h3>
              <p className="text-sm text-green-700">All systems operational</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900">Voice Service</h3>
              <p className="text-sm text-green-700">All systems operational</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900">Calendar Sync</h3>
              <p className="text-sm text-green-700">All systems operational</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Still Need Help?</h2>
              <p className="mt-2 text-gray-600">Send us a message and we'll get back to you within 4 hours</p>
            </div>
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option>General Question</option>
                  <option>Technical Support</option>
                  <option>Billing Question</option>
                  <option>Feature Request</option>
                  <option>Integration Help</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your question or issue..."
                ></textarea>
              </div>
              
              <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700">
                Send Message
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white">Ready to Get Started?</h2>
          <p className="mt-4 text-xl text-blue-100">
            Join hundreds of businesses using AI to automate their bookings
          </p>
          <div className="mt-8">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100">
              Start Your 14-Day Free Trial
            </button>
            <p className="mt-4 text-blue-100">
              No credit card required • Full support included
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportFAQPage;